import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ContentChildren,
  DestroyRef,
  EventEmitter,
  inject,
  input,
  Input,
  OnInit,
  Output,
  Pipe,
  PipeTransform,
  QueryList,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup } from '@angular/forms';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { distinctUntilChanged, filter, map, Subject, switchMap, tap } from 'rxjs';

import { GuestDTO, GuestsService, SpaProductAgeRestrictionDTO, SpaProductsService, SpaSimpleProductDTO } from '@api';
import { AutocompleteColumn, AutocompleteRow } from '@components/autocomplete-search/autocomplete-search.component';
import { PositionedList, PositionedListItem } from '@components/spa-list/spa-list.component';
import { FormatDatePipe } from '@pipe';
import { BookingCompanionsService, CompanionMapType } from '@services/booking-companions.service';
import { BookingUtils } from '@utils';

import { SpaListTemplateComponent } from '../spa-list/spa-list-template/spa-list-template.component';

@UntilDestroy()
@Component({
  selector: 'spa-companions-spa-list',
  templateUrl: 'companions-spa-list.component.html',
  styleUrls: ['companions-spa-list.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanionsSpaListComponent<T> implements OnInit {
  private guestsService = inject(GuestsService);
  private formatDate = inject(FormatDatePipe);
  protected readonly bookingCompanionService = inject(BookingCompanionsService);
  private spaProductsService = inject(SpaProductsService);
  private readonly destroyRef = inject(DestroyRef);

  @Input() rows: T[] | null = null;
  @Input() rowsIdsWithHint: string[] | null = null;
  @Input() columns: PositionedList<T> | null = null;
  @Input() selectedRow: string | null = null;
  @Input() rowId!: keyof T;
  readonly mainGuestIdsWithAgeIssue = input<string[] | null>(null);
  readonly companionIdsWithAgeIssue = input<string[]>([]);
  @Input() isDifferentPayer = false;
  @Input() interactive = false;
  @Input() hintText = '';
  @Output() selectElement = new EventEmitter<T | undefined>();
  @Output() removeElement = new EventEmitter<number | undefined>();
  @ContentChildren(SpaListTemplateComponent) columnTemplates!: QueryList<SpaListTemplateComponent>;

  @Input() checkboxSelectionType: 'single' | 'multiple' | 'none' = 'none';

  protected readonly isSearching = signal<Record<string, boolean>>({});
  loadGuest$ = new Subject<{ searchTerm: string; rowindex: number; inputindex: number } | undefined>();
  guests: GuestDTO[] | null = null;

  form = new FormGroup({
    companions: new FormArray<
      FormGroup<{
        ageRestriction: FormControl<SpaProductAgeRestrictionDTO | null>;
        price: FormControl<number | null>;
      }>
    >([]),
  });

  numberOfCompanions = signal(0);
  productID = signal<string | null>('');
  companion: SpaSimpleProductDTO | null = null;

  protected readonly simpleProduct = signal<SpaSimpleProductDTO | undefined>(undefined);

  protected readonly mainGuestAgeIssueIdMap = computed<Record<string, boolean>>(() => {
    const map: Record<string, boolean> = {};
    for (const id of this.mainGuestIdsWithAgeIssue() ?? []) {
      map[id] = true;
    }
    return map;
  });
  protected readonly companionAgeIssueIdMap = computed<Record<string, boolean>>(() => {
    const map: Record<string, boolean> = {};
    for (const id of this.companionIdsWithAgeIssue()) {
      map[id] = true;
    }
    return map;
  });

  protected readonly guestSearchResultsMap = signal<Record<string, AutocompleteRow<GuestDTO>[]>>({});

  autocompleteColumns: AutocompleteColumn<GuestDTO>[] = [
    {
      key: 'imageUri',
      value: () => '',
    },
    {
      key: 'lastName',
      widthInPercent: '30%',
    },
    {
      key: 'firstName',
      widthInPercent: '30%',
    },
    {
      key: 'birthdate',
      value: (row) => this.formatDate.transform(row.birthdate, 'DD.MM.YYYY'),
      widthInPercent: '20%',
    },
    {
      key: 'cabinNumber',
      widthInPercent: '10%',
    },
  ];

  ngOnInit() {
    this.initLoadGuest();
  }

  private updateFormValidityOnChanges() {
    let value = 0;

    if (this.numberOfCompanions() > 0) {
      value = this.numberOfCompanions();
    }

    const companions = this.form.controls.companions;

    if (!value) {
      companions.clear();
      return;
    }
    while (companions.length < value) {
      companions.push(
        new FormGroup({
          ageRestriction: new FormControl<SpaProductAgeRestrictionDTO | null>(null),
          price: new FormControl<number | null>(0),
        }),
      );
    }
    while (companions.length > value) {
      companions.removeAt(companions.length - 1);
    }

    requestAnimationFrame(() => {
      this.form.controls.companions.updateValueAndValidity();
    });
  }

  async onSelectCompanions(guests: GuestDTO[], rowIndex: number, columnIndex: number) {
    if (!guests) {
      return;
    }

    const key = `${rowIndex}-${columnIndex}`;

    const selectedCompanionsMap = this.bookingCompanionService.s_companionMap();

    selectedCompanionsMap[key] = guests[0];

    this.bookingCompanionService.s_companionMap.set({ ...selectedCompanionsMap });
  }

  async removeSelectedCompanion(rowIndex: number, columnIndex: number) {
    this.guestSearchResultsMap.update((map) => {
      const copy = { ...map };
      this.deleteCompanion(copy, rowIndex, columnIndex);
      return copy;
    });

    this.bookingCompanionService.s_companionMap.update((companionMap) => {
      this.deleteCompanion(companionMap, rowIndex, columnIndex);
      return { ...companionMap };
    });
  }

  deleteCompanion(map: Record<string, unknown>, rowIndex: number, columnIndex: number) {
    let currentIndex = this.keyForCompanion(rowIndex, columnIndex);
    delete map[currentIndex];

    let nextIndex: string;
    for (
      let nextColumnIndex = columnIndex + 1;
      map[(nextIndex = this.keyForCompanion(rowIndex, nextColumnIndex))];
      nextColumnIndex++
    ) {
      map[currentIndex] = map[nextIndex];
      delete map[nextIndex];
      currentIndex = nextIndex;
    }
  }

  keyForCompanion(rowIndex: number, columnIndex: number) {
    return `${rowIndex}-${columnIndex}`;
  }

  loadGuests(searchTerm: string, rowindex: number, inputindex: number): void {
    this.loadGuest$.next({ searchTerm, rowindex, inputindex });
  }

  initLoadGuest() {
    this.loadGuest$
      .pipe(
        distinctUntilChanged(
          (a, b) => a?.searchTerm === b?.searchTerm && a?.rowindex === b?.rowindex && a?.inputindex === b?.inputindex,
        ),
        filter((searchTerm) => !!searchTerm),
        filter(({ searchTerm }) => !!searchTerm),
        tap(({ rowindex, inputindex }) => {
          const key = `${rowindex}-${inputindex}`;
          this.isSearching.update((isSearching) => ({ ...isSearching, [key]: true }));
        }),
        switchMap(({ searchTerm, rowindex, inputindex }) =>
          this.guestsService.getGuests(searchTerm).pipe(map((results) => ({ results, rowindex, inputindex }))),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ results, rowindex, inputindex }) => {
        const key = `${rowindex}-${inputindex}`;
        const searchResults = results.elements.map((companion) => {
          const checked = this.isChecked(companion);

          let isAlreadyGuest = false;

          this.rows?.map((guest) => {
            isAlreadyGuest = this.isCheckedInGuestsList(guest, companion);
          });

          if (!isAlreadyGuest) {
            return {
              isChecked: checked,
              alreadySelected: checked,
              data: companion,
            };
          }
          return {
            isChecked: true,
            alreadySelected: true,
            data: companion,
          };
        });
        this.guestSearchResultsMap.update((map) => ({ ...map, [key]: searchResults }));
        this.isSearching.update((isSearching) => ({ ...isSearching, [key]: false }));
      });
  }

  isChecked(guest: GuestDTO) {
    return Object.values(this.bookingCompanionService.s_companionMap()).some(
      (companion) => companion.guestId === guest.guestId,
    );
  }

  isCheckedInGuestsList(guest: T, companion: GuestDTO) {
    return (companion as { guestId?: string }).guestId === (guest as { guestId?: string }).guestId;
  }

  constructor() {
    this.bookingCompanionService.currentProductID.pipe(untilDestroyed(this)).subscribe((currentProductID) => {
      this.productID.set(currentProductID);

      const id = this.productID();
      if (id) {
        this.spaProductsService.getSimpleProduct(id).subscribe((product) => {
          this.simpleProduct.set(product);
          this.numberOfCompanions.set(product.companions.length);

          this.bookingCompanionService.setNumberOfCompanions(this.numberOfCompanions());
          this.updateFormValidityOnChanges();
        });
      }
    });
  }

  rowContainsHint(rowId2: T[keyof T]): boolean {
    return !!this.rowsIdsWithHint?.find((rowId1) => rowId1 === rowId2);
  }

  getTemplate(key: string | number | symbol) {
    return this.columnTemplates.find((t) => t.templateKey === key);
  }

  cellValue(row: T, column: PositionedListItem<T>) {
    if (column.value) {
      return column.value(row);
    }

    // @ts-expect-error -- key is a generic keyof T, TS cannot verify index access
    return row[column.key];
  }

  onClickRow(row: T): void {
    this.selectElement.emit(row);
  }

  removeSelectedEmployee(index: number): void {
    this.removeElement.emit(index);
  }

  translationKey(column: PositionedListItem<T>) {
    if (column.translationKey) {
      return column.translationKey;
    }
    return column.key ? ['label.', column.key].join('') : null;
  }

  protected readonly BookingUtils = BookingUtils;
}

@Pipe({
  name: 'filledCompanions',
})
export class FilledCompanionsPipe implements PipeTransform {
  transform(map: CompanionMapType, rowIndex: number): number {
    return Object.entries(map)
      .filter(([key]) => key.startsWith(`${rowIndex}-`))
      .filter(([value]) => !!value).length;
  }
}
